/* Bold Booking — magazine cinema booking flow.
   Dark canvas, oversized type, ACT I / ACT II / ACT III stepper.
   Matches the Bold home/services aesthetic (full-bleed, tabular nums,
   colossal headlines, gold accent on the active state). */

import { useEffect, useRef } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useBookingFlow, startingPrice, formatPrice } from "@/hooks/useBookingFlow";
import BookingTermsAcceptance from "@/components/BookingTermsAcceptance";

const ACTS = [
  { n: "I",   key: "package", label: "Package" },
  { n: "II",  key: "schedule", label: "Schedule" },
  { n: "III", key: "details", label: "Details" },
];

export default function BoldBooking() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const f = useBookingFlow({ totalSteps: 3 });

  useEffect(() => {
    document.title = `Book · ${studioName}`;
  }, [studioName]);

  // Bold collapses Date + Customer details into a single Act II (schedule)
  // and pushes contact details to Act III. Map the shared hook's 4-step
  // model onto Bold's 3-act narrative.
  const stage = f.step <= 1 ? 1 : f.step === 2 ? 2 : 3;

  // Smooth-scroll the stage content into view on every act change so
  // mobile visitors aren't stranded near the bottom action bar after
  // tapping Continue. See Editorial Booking for the full rationale.
  const stageRef = useRef(null);
  const firstStageMount = useRef(true);
  useEffect(() => {
    if (firstStageMount.current) {
      firstStageMount.current = false;
      return;
    }
    const el = stageRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [stage]);

  function next() {
    if (stage === 1) f.setStep(2);
    else if (stage === 2) f.setStep(3);
    else if (stage === 3) f.submit();
  }
  function back() {
    if (stage === 1) return;
    f.setStep(stage === 2 ? 1 : 2);
  }

  return (
    <div className="bg-[color:var(--tenant-bg,#0A0908)] text-stone-100 min-h-screen" data-testid="bold-booking" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24">
        {/* Eyebrow + 3-act stepper */}
        <div className="text-[10px] uppercase tracking-[0.45em] text-stone-400 mb-4">RESERVE A SITTING</div>
        <h1 className="leading-[0.9] tracking-tight" style={{ fontFamily: "var(--tenant-serif, serif)", fontSize: "clamp(3.5rem, 9vw, 7.5rem)" }}>
          A Booking <em className="italic font-light text-[color:var(--tenant-accent,#C9A961)]">in Three Acts</em>.
        </h1>

        <div className="mt-14 grid grid-cols-3 gap-4 lg:gap-10 border-y border-[color:var(--tenant-fg-line,rgba(245,242,236,0.15))] py-6" data-testid="bold-booking-acts">
          {ACTS.map((act, i) => {
            const active = stage === i + 1;
            const done = stage > i + 1;
            return (
              <div key={act.n} className={`flex items-baseline gap-4 ${active ? "text-[color:var(--tenant-accent,#C9A961)]" : done ? "text-stone-200" : "text-stone-500"}`}>
                <div className="font-serif text-3xl lg:text-5xl tabular-nums" style={{ fontFamily: "var(--tenant-serif, serif)" }}>{act.n}</div>
                <div className="text-[10px] uppercase tracking-[0.32em]">ACT {act.n} · {act.label}</div>
              </div>
            );
          })}
        </div>

        {/* Stage content */}
        <div ref={stageRef} className="mt-14 scroll-mt-24">
          {stage === 1 && (
            <div data-testid="bold-step-package">
              <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-6">CHOOSE A SITTING</div>
              <div className="grid lg:grid-cols-[1fr_440px] gap-10">
                <div className="divide-y divide-[color:var(--tenant-fg-line,rgba(245,242,236,0.12))]">
                  {f.services.map((s, i) => {
                    const sel = (s.id === f.selected?.id) || (s.slug === f.selected?.slug);
                    const price = startingPrice(s);
                    return (
                      <button key={s.id || i} type="button"
                        onClick={() => f.setSelectedId(s.id || s.slug)}
                        data-testid={`bold-package-${i}`}
                        className={`group w-full text-left py-7 flex items-start justify-between gap-6 transition-colors ${sel ? "text-stone-50" : "text-stone-300 hover:text-stone-50"}`}>
                        <div className="flex-1">
                          <div className="text-[10px] uppercase tracking-[0.38em] text-[color:var(--tenant-accent,#C9A961)] mb-2">{s.eyebrow || (s.category || "STUDIO").toUpperCase()}</div>
                          <div className="leading-[0.95] tracking-tight" style={{ fontFamily: "var(--tenant-serif, serif)", fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}>{s.name}</div>
                          <p className="mt-3 max-w-xl text-stone-400 text-sm">{s.description}</p>
                        </div>
                        <div className="text-right shrink-0 pt-2">
                          <div className="font-serif italic text-2xl text-stone-100" style={{ fontFamily: "var(--tenant-serif, serif)" }}>{formatPrice(price) || "by request"}</div>
                          <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mt-1">{s.duration || "90 MIN"}</div>
                          <div className={`mt-3 text-[10px] uppercase tracking-[0.32em] ${sel ? "text-[color:var(--tenant-accent,#C9A961)]" : "text-stone-600"}`}>{sel ? "✓ SELECTED" : "SELECT →"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Right-rail tier picker — appears once a service is selected */}
                {f.selected?.tiers?.length > 0 && (
                  <aside className="border border-[color:var(--tenant-fg-line,rgba(245,242,236,0.15))] p-7 lg:p-9 self-start sticky top-8" data-testid="bold-tier-rail">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-2">CHOOSE A TIER</div>
                    <div className="font-serif text-3xl mb-6" style={{ fontFamily: "var(--tenant-serif, serif)" }}>{f.selected.name}</div>
                    <ul className="divide-y divide-[color:var(--tenant-fg-line,rgba(245,242,236,0.10))]">
                      {f.selected.tiers.map((t) => {
                        const active = t.name === f.tierName;
                        return (
                          <li key={t.name}>
                            <button type="button" onClick={() => f.setTierName(t.name)} data-testid={`bold-tier-${t.name}`}
                              className={`w-full py-4 flex items-baseline justify-between gap-3 text-left transition-colors ${active ? "text-[color:var(--tenant-accent,#C9A961)]" : "text-stone-300 hover:text-stone-50"}`}>
                              <div>
                                <div className="font-serif italic text-xl" style={{ fontFamily: "var(--tenant-serif, serif)" }}>{t.name}</div>
                                {t.description && <div className="text-xs text-stone-500 mt-0.5">{t.description}</div>}
                              </div>
                              <div className="text-right">
                                <div className="font-serif text-xl tabular-nums" style={{ fontFamily: "var(--tenant-serif, serif)" }}>{formatPrice(Number(t.price))}</div>
                                <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500">{t.duration_minutes ? `${t.duration_minutes} MIN` : ""}</div>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </aside>
                )}
              </div>
            </div>
          )}

          {stage === 2 && (
            <div data-testid="bold-step-schedule" className="max-w-3xl">
              <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-6">CHOOSE WHEN</div>
              <div className="grid sm:grid-cols-2 gap-8">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#C9A961)]">Date</span>
                  <input type="date" value={f.date} onChange={(e) => f.setDate(e.target.value)} data-testid="bold-date"
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] focus:border-[color:var(--tenant-accent,#C9A961)] py-3 mt-3 outline-none text-2xl" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#C9A961)]">Window</span>
                  <select value={f.time} onChange={(e) => f.setTime(e.target.value)} data-testid="bold-time"
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] focus:border-[color:var(--tenant-accent,#C9A961)] py-3 mt-3 outline-none text-2xl">
                    {["09:00","10:00","11:00","13:00","14:00","15:00","16:00"].map((t) => <option key={t} value={t} className="bg-stone-900">{t}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}

          {stage === 3 && (
            <div data-testid="bold-step-details" className="grid lg:grid-cols-[1fr_440px] gap-10">
              <div className="space-y-7 max-w-2xl">
                <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500">YOUR DETAILS</div>
                {[
                  { k: "name",  l: "Name",            t: "text",  ph: "Jane Doe" },
                  { k: "email", l: "Email",           t: "email", ph: "jane@studio.com" },
                  { k: "phone", l: "Phone (optional)", t: "tel",  ph: "+1 555 0100" },
                ].map((field) => (
                  <label key={field.k} className="block">
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#C9A961)]">{field.l}</span>
                    <input type={field.t} value={f.details[field.k]} placeholder={field.ph} data-testid={`bold-${field.k}`}
                      onChange={(e) => f.setDetails({ ...f.details, [field.k]: e.target.value })}
                      className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] focus:border-[color:var(--tenant-accent,#C9A961)] py-3 mt-3 outline-none text-xl placeholder:text-stone-600" />
                  </label>
                ))}
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#C9A961)]">A short note</span>
                  <textarea rows={4} value={f.details.notes} placeholder="Tell us what you'd like remembered."
                    onChange={(e) => f.setDetails({ ...f.details, notes: e.target.value })}
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] focus:border-[color:var(--tenant-accent,#C9A961)] py-3 mt-3 outline-none text-lg placeholder:text-stone-600 resize-none" data-testid="bold-notes" />
                </label>
                <div className="pt-4">
                  <BookingTermsAcceptance
                    policyText={f.policyText}
                    checked={f.termsAccepted}
                    onChange={f.setTermsAccepted}
                    testidPrefix="bold-booking-terms"
                  />
                </div>
              </div>
              {/* Summary rail */}
              <aside className="border border-[color:var(--tenant-fg-line,rgba(245,242,236,0.15))] p-7 lg:p-9 self-start sticky top-8" data-testid="bold-summary-rail">
                <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-5">YOUR SITTING</div>
                <dl className="space-y-4">
                  {[
                    ["Service",  f.selected?.name || "—"],
                    ["Tier",     f.selectedTier?.name || "—"],
                    ["Date",     f.date || "—"],
                    ["Window",   f.time || "—"],
                    ["Total",    formatPrice(Number(f.selectedTier?.price)) || "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-4 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.10))] pb-3">
                      <dt className="text-[10px] uppercase tracking-[0.32em] text-stone-500">{k}</dt>
                      <dd className="font-serif italic text-xl text-stone-100 text-right" style={{ fontFamily: "var(--tenant-serif, serif)" }}>{v}</dd>
                    </div>
                  ))}
                </dl>
              </aside>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="mt-14 flex items-center justify-between gap-4">
          <button type="button" onClick={back} disabled={stage === 1} data-testid="bold-booking-back"
            className="text-[10px] uppercase tracking-[0.32em] text-stone-400 hover:text-stone-100 disabled:opacity-30 disabled:hover:text-stone-400 transition-colors">
            {stage === 1 ? "" : "← Back"}
          </button>
          <button type="button" onClick={next} disabled={!f.canForward || f.submitting} data-testid="bold-booking-next"
            className="inline-flex items-center gap-2 px-7 h-12 bg-[color:var(--tenant-accent,#C9A961)] text-stone-950 text-[10px] uppercase tracking-[0.38em] font-medium hover:opacity-90 disabled:opacity-40 transition-opacity">
            {f.submitting ? "Sending…" : stage < 3 ? "Continue →" : "Send request →"}
          </button>
        </div>
      </div>
    </div>
  );
}
